/**
 * Conversation List Component
 * 
 * Displays list of all conversations with search functionality
 * and option to start new chats.
 */

import React, { useState, useMemo } from 'react';
import { Input, Button, Empty, Spin, Tabs, Space, Badge } from 'antd';
import { SearchOutlined, PlusOutlined, InboxOutlined, FilterOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { Conversation } from '@municipality/types';
import { ConversationItem } from './ConversationItem';
import { useResponsive } from '@municipality/ui';

interface ConversationListProps {
  conversations: Conversation[];
  activeConversationId: string | null;
  currentUserId: string;
  isLoading: boolean;
  onSelectConversation: (conversationId: string) => void;
  onNewChat: () => void;
  onMute?: (conversationId: string, userId: string, isMuted: boolean) => Promise<void>;
  onArchive?: (conversationId: string, userId: string, isArchived: boolean) => Promise<void>;
  onPin?: (conversationId: string, userId: string, isPinned: boolean) => Promise<void>;
  onOpenSearch?: () => void;
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fff;
  border-right: 1px solid #f0f0f0;
`;

const ListHeader = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? '12px' : '16px')};
  border-bottom: 1px solid #f0f0f0;
`;

const SearchContainer = styled.div`
  margin-bottom: 12px;
`;

const ListContent = styled.div<{ $isMobile: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: ${({ $isMobile }) => ($isMobile ? '4px' : '8px')};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  color: #999;
`;

const TabsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
`;

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversationId,
  currentUserId,
  isLoading,
  onSelectConversation,
  onNewChat,
  onMute,
  onArchive,
  onPin,
  onOpenSearch,
}) => {
  const { t } = useTranslation('chat');
  const { isMobile } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');

  // Separate conversations into pinned, active, and archived
  const { pinnedConversations, activeConversations, archivedConversations, archivedUnreadCount } = useMemo(() => {
    const pinned: Conversation[] = [];
    const active: Conversation[] = [];
    const archived: Conversation[] = [];

    conversations.forEach((conv) => {
      const isArchived = conv.isArchived?.[currentUserId] || false;
      const isPinned = conv.pinnedBy?.includes(currentUserId) || false;

      if (isArchived) {
        archived.push(conv);
      } else if (isPinned) {
        pinned.push(conv);
      } else {
        active.push(conv);
      }
    });

    // Sort by last message timestamp (most recent first)
    const sortByLastMessage = (a: Conversation, b: Conversation) => {
      const aTime = a.lastMessage?.timestamp?.getTime() || 0;
      const bTime = b.lastMessage?.timestamp?.getTime() || 0;
      return bTime - aTime;
    };

    pinned.sort(sortByLastMessage);
    active.sort(sortByLastMessage);
    archived.sort(sortByLastMessage);

    // Calculate total unread count for archived conversations
    const archivedUnread = archived.reduce((sum, conv) => {
      return sum + (conv.unreadCount[currentUserId] || 0);
    }, 0);

    return { 
      pinnedConversations: pinned, 
      activeConversations: active, 
      archivedConversations: archived,
      archivedUnreadCount: archivedUnread
    };
  }, [conversations, currentUserId]);

  // Filter conversations based on search query and active tab
  const getFilteredConversations = (convs: Conversation[]) => {
    if (!searchQuery) return convs;

    const query = searchQuery.toLowerCase();

    return convs.filter((conv) => {
      // Search in conversation name
      if (conv.name?.toLowerCase().includes(query)) {
        return true;
      }

      // Search in participant names
      const participantNames = Object.values(conv.participantDetails)
        .map((p) => p.name.toLowerCase())
        .join(' ');
      
      if (participantNames.includes(query)) {
        return true;
      }

      // Search in last message
      if (conv.lastMessage?.content.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  };

  const displayConversations = activeTab === 'active' 
    ? [...pinnedConversations, ...activeConversations]
    : archivedConversations;

  const filteredConversations = getFilteredConversations(displayConversations);

  return (
    <ListContainer>
      <ListHeader $isMobile={isMobile}>
        <SearchContainer>
          <Input
            placeholder={t('conversationList.searchPlaceholder') || "Search conversations..."}
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            suffix={
              onOpenSearch ? (
                <Button
                  type="text"
                  size="small"
                  icon={<FilterOutlined />}
                  onClick={onOpenSearch}
                  title={t('search.title') || 'Advanced Search'}
                />
              ) : undefined
            }
          />
        </SearchContainer>
        
        <Space style={{ width: '100%' }} direction="vertical">
          {onOpenSearch && (
            <Button
              icon={<FilterOutlined />}
              onClick={onOpenSearch}
              block
            >
              {t('search.title') || 'Advanced Search'}
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onNewChat}
            block
          >
            {t('conversationList.newChat') || "New Chat"}
          </Button>
        </Space>
      </ListHeader>

      <TabsContainer>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as 'active' | 'archived')}
          items={[
          {
            key: 'active',
            label: t('conversationList.active') || 'Active',
            children: (
              <ListContent $isMobile={isMobile}>
                {isLoading ? (
                  <EmptyState>
                    <Spin size="large" />
                  </EmptyState>
                ) : filteredConversations.length === 0 ? (
                  <EmptyState>
                    <Empty
                      description={
                        searchQuery
                          ? t('conversationList.noResults') || 'No conversations found'
                          : t('conversationList.noConversations') || 'No conversations yet'
                      }
                    >
                      {!searchQuery && (
                        <Button type="primary" onClick={onNewChat}>
                          {t('conversationList.startConversation') || 'Start a conversation'}
                        </Button>
                      )}
                    </Empty>
                  </EmptyState>
                ) : (
                  <>
                    {pinnedConversations.length > 0 && activeTab === 'active' && (
                      <>
                        {getFilteredConversations(pinnedConversations).map((conversation) => (
                          <ConversationItem
                            key={conversation.id}
                            conversation={conversation}
                            isActive={conversation.id === activeConversationId}
                            currentUserId={currentUserId}
                            onClick={() => onSelectConversation(conversation.id)}
                            onMute={onMute ? (id, muted) => onMute(id, currentUserId, muted) : undefined}
                            onArchive={onArchive ? (id, archived) => onArchive(id, currentUserId, archived) : undefined}
                            onPin={onPin ? (id, pinned) => onPin(id, currentUserId, pinned) : undefined}
                          />
                        ))}
                        {getFilteredConversations(activeConversations).length > 0 && (
                          <div style={{ height: 8 }} />
                        )}
                      </>
                    )}
                    {getFilteredConversations(activeTab === 'active' ? activeConversations : archivedConversations).map((conversation) => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        isActive={conversation.id === activeConversationId}
                        currentUserId={currentUserId}
                        onClick={() => onSelectConversation(conversation.id)}
                        onMute={onMute ? (id, muted) => onMute(id, currentUserId, muted) : undefined}
                        onArchive={onArchive ? (id, archived) => onArchive(id, currentUserId, archived) : undefined}
                        onPin={onPin ? (id, pinned) => onPin(id, currentUserId, pinned) : undefined}
                      />
                    ))}
                  </>
                )}
              </ListContent>
            ),
          },
          {
            key: 'archived',
            label: (
              <span>
                <InboxOutlined style={{ marginRight: 4 }} />
                {t('conversationList.archived') || 'Archived'}
                {archivedUnreadCount > 0 ? (
                  <Badge 
                    count={archivedUnreadCount} 
                    style={{ marginLeft: 8 }}
                    offset={[0, 0]}
                  />
                ) : archivedConversations.length > 0 ? (
                  <span style={{ marginLeft: 4, color: '#999', fontSize: '12px' }}>
                    ({archivedConversations.length})
                  </span>
                ) : null}
              </span>
            ),
            children: (
              <ListContent $isMobile={isMobile}>
                {archivedConversations.length === 0 ? (
                  <EmptyState>
                    <Empty
                      description={t('conversationList.noArchived') || 'No archived conversations'}
                    />
                  </EmptyState>
                ) : (
                  filteredConversations.map((conversation) => (
                    <ConversationItem
                      key={conversation.id}
                      conversation={conversation}
                      isActive={conversation.id === activeConversationId}
                      currentUserId={currentUserId}
                      onClick={() => onSelectConversation(conversation.id)}
                      onMute={onMute ? (id, muted) => onMute(id, currentUserId, muted) : undefined}
                      onArchive={onArchive ? (id, archived) => onArchive(id, currentUserId, archived) : undefined}
                      onPin={onPin ? (id, pinned) => onPin(id, currentUserId, pinned) : undefined}
                    />
                  ))
                )}
              </ListContent>
            ),
          },
          ]}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          tabBarStyle={{ margin: 0, padding: '0 16px' }}
        />
      </TabsContainer>
    </ListContainer>
  );
};

